import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@nimbitlabs/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Alert, AlertDescription } from '@nimbitlabs/ui';
import { CheckCircle2, XCircle, Loader2, AlertTriangle } from 'lucide-react';

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState<any>(null);
  const [wrongUser, setWrongUser] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);

  const invitationId = searchParams.get('id');
  const token = searchParams.get('token');
  const email = searchParams.get('email'); // Email from invitation link

  useEffect(() => {
    if (!invitationId && !token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    if (!user) {
      // Not logged in - redirect to login with return URL
      const returnUrl = `/accept-invitation?${searchParams.toString()}`;
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }

    // Check if logged in user matches invited email
    if (email && user.email !== email) {
      setInvitedEmail(email);
      setWrongUser(true);
      setLoading(false);
      return;
    }

    acceptInvitation();
  }, [user, invitationId, token, email]);

  const handleSwitchUser = async () => {
    try {
      // Logout current user
      await logout();
      
      // Redirect to login with return URL
      const returnUrl = `/accept-invitation?${searchParams.toString()}`;
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
    } catch (err) {
      setError('Failed to logout. Please try again.');
    }
  };

  const acceptInvitation = async () => {
    try {
      setLoading(true);
      setError(null);

      // If we have a token, we need to verify it first
      // For now, assume we have the invitation ID
      const response = await fetch(`/api/invitations/${invitationId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to accept invitation');
      }

      const data = await response.json();
      setInvitationDetails(data);
      setSuccess(true);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Processing invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (wrongUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-yellow-500" />
              <CardTitle>Wrong Account</CardTitle>
            </div>
            <CardDescription>You're logged in as a different user</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>
                This invitation was sent to <strong>{invitedEmail}</strong>, but you're currently logged in as <strong>{user?.email}</strong>.
              </AlertDescription>
            </Alert>
            <p className="text-sm text-muted-foreground">
              To accept this invitation, you need to log out and sign in with the invited email address.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={handleSwitchUser} className="w-full">
                Switch to {invitedEmail}
              </Button>
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full">
                Stay as {user?.email}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <XCircle className="h-6 w-6 text-destructive" />
              <CardTitle>Invitation Error</CardTitle>
            </div>
            <CardDescription>There was a problem with your invitation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              <CardTitle>Invitation Accepted!</CardTitle>
            </div>
            <CardDescription>You've successfully joined the account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Redirecting you to the dashboard...
            </p>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to Dashboard Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
